import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import HeroSection from './components/HeroSection';
import CategoryTabs from './components/CategoryTabs';
import VideoGrid from './components/VideoGrid';
import LatestSection from './components/LatestSection';
import { allVideos, featuredVideos } from '@/mocks/videos';

export default function Home() {
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredVideos = activeCategory === 'all'
    ? allVideos
    : allVideos.filter(v => v.categorySlug === activeCategory);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '91在线视频 - 国产电影电视剧全集免费高清在线观看',
    description: '91在线视频汇聚91年国产影片视频、91国产视频在线观看等国产影视资源，免费在线观看高清不缓冲。',
    url: 'https://www.91onlinevideo.com',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredVideos.slice(0, 10).map((video, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Movie',
          name: video.title,
          url: `https://www.91onlinevideo.com/article/${video.id}`,
          image: video.thumbnail,
          dateCreated: video.releaseDate,
          director: { '@type': 'Person', name: video.director },
        },
      })),
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <Navbar />
      <main>
        <HeroSection />

        <section className="w-full px-4 md:px-6 py-8 md:py-10">
          <CategoryTabs
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
          />
        </section>

        <section className="w-full px-4 md:px-6 pb-8">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-900">
              {activeCategory === 'all' ? (
                <><i className="ri-fire-line text-primary-500 mr-2"></i>热门推荐</>
              ) : (
                <><i className="ri-play-list-2-line text-primary-500 mr-2"></i>分类浏览</>
              )}
            </h2>
            <span className="text-xs text-foreground-500">
              共 {filteredVideos.length} 部
            </span>
          </div>
          <VideoGrid videos={filteredVideos} />
        </section>

        <div className="w-full py-8 md:py-10 bg-background-50">
          <LatestSection />
        </div>

        <section className="w-full px-4 md:px-6 py-8 md:py-10">
          <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-900 mb-5">
            <i className="ri-star-smile-line text-accent-500 mr-2"></i>
            精选推荐
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {featuredVideos.map((video) => (
              <a
                key={video.id}
                href={`/article/${video.id}`}
                className="group flex gap-4 p-4 rounded-xl bg-background-100 hover:bg-background-200 transition-colors"
              >
                <div className="relative w-32 md:w-40 flex-shrink-0 aspect-video rounded-lg overflow-hidden bg-background-300">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    title={`${video.title} - 91在线视频`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-8 h-8 bg-primary-500/90 rounded-full flex items-center justify-center">
                      <i className="ri-play-fill text-white text-sm ml-0.5"></i>
                    </div>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-foreground-800 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {video.title}
                  </h3>
                  <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-500 flex-wrap">
                    <span className="text-accent-600 font-medium flex items-center gap-0.5">
                      <i className="ri-star-fill text-xs"></i>{video.rating}
                    </span>
                    <span>{video.year}</span>
                    <span>{video.type}</span>
                  </div>
                  <p className="text-xs text-foreground-500 mt-2 line-clamp-2">{video.description}</p>
                </div>
              </a>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-50">
          <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-900 mb-5">
            <i className="ri-question-answer-line text-secondary-600 mr-2"></i>
            常见问题
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
            {[
              { q: '91在线视频是否需要注册？', a: '不需要。所有影视内容均免费在线观看，无需注册登录，即点即看。' },
              { q: '视频播放卡顿怎么办？', a: '我们采用CDN加速技术，支持自适应码率。如遇卡顿，建议检查网络或切换清晰度。' },
              { q: '是否有移动端App？', a: '网站采用响应式设计，完美适配手机、平板等移动设备，浏览器直接访问即可。' },
              { q: '视频更新频率如何？', a: '每天更新最新国产影视内容，确保您第一时间观看到最新电影、电视剧和综艺。' },
            ].map((faq, index) => (
              <div key={index} className="p-4 rounded-lg bg-background-100">
                <h4 className="text-sm font-semibold text-foreground-800 mb-2">
                  <a href={`/about#faq-${index}`}>{faq.q}</a>
                </h4>
                <p className="text-xs text-foreground-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}